import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  Search, 
  Users, 
  UserCheck,
  UserX,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { GuestMatch, GuestSummary } from '@/hooks/useStripeCleanup';
import { GuestMatchesTable } from './GuestMatchesTable';

interface GuestMatchingSectionProps {
  isAnalyzing: boolean;
  isExecuting: boolean;
  progress: number;
  progressText: string;
  guestMatches: GuestMatch[];
  unmatchedGuests: { id: string; email: string | null; name: string | null }[];
  guestSummary: GuestSummary | null;
  selectedGuestIds: Set<string>;
  onAnalyze: () => void;
  onExecuteMerge: (guestIds: string[]) => void;
  onToggleSelection: (guestId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

export function GuestMatchingSection({
  isAnalyzing,
  isExecuting,
  progress,
  progressText,
  guestMatches,
  unmatchedGuests,
  guestSummary,
  selectedGuestIds,
  onAnalyze,
  onExecuteMerge,
  onToggleSelection,
  onSelectAll,
  onClearSelection,
}: GuestMatchingSectionProps) {
  return (
    <div className="space-y-6">
      {/* Analysis Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Matching de Guests por Nome</CardTitle>
          <CardDescription>
            Encontre guests que podem pertencer a clientes reais baseado em partes do nome no email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Button 
              onClick={onAnalyze} 
              disabled={isAnalyzing}
              size="lg"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analisando...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Analisar Guests
                </>
              )}
            </Button>
          </div>

          {isAnalyzing && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-muted-foreground">{progressText}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {guestSummary && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{guestSummary.total_guests_with_email}</p>
                  <p className="text-xs text-muted-foreground">Guests com Email</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                  <Users className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{guestSummary.total_real_customers}</p>
                  <p className="text-xs text-muted-foreground">Clientes Reais</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                  <UserCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{guestSummary.total_matched}</p>
                  <p className="text-xs text-muted-foreground">Matches Encontrados</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                  <UserX className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{guestSummary.total_unmatched}</p>
                  <p className="text-xs text-muted-foreground">Sem Match</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Matches Table */}
      {guestMatches.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Possíveis Matches</CardTitle>
              <CardDescription>
                {guestMatches.length} matches encontrados • {selectedGuestIds.size} selecionados
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={selectedGuestIds.size === guestMatches.length ? onClearSelection : onSelectAll}
              >
                {selectedGuestIds.size === guestMatches.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={selectedGuestIds.size === 0 || isExecuting}
                onClick={() => onExecuteMerge(Array.from(selectedGuestIds))}
              >
                {isExecuting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Merging...
                  </>
                ) : (
                  `Confirmar Selecionados (${selectedGuestIds.size})`
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <GuestMatchesTable
              matches={guestMatches}
              selectedIds={selectedGuestIds}
              onToggleSelection={onToggleSelection}
            />
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isAnalyzing && !guestSummary && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhuma análise realizada</h3>
            <p className="text-muted-foreground text-sm text-center max-w-md mb-4">
              Clique em "Analisar Guests" para encontrar guests que podem ser associados a clientes reais
            </p>
          </CardContent>
        </Card>
      )}

      {/* No Matches Found */}
      {guestSummary && guestMatches.length === 0 && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400 mb-4" />
            <h3 className="text-lg font-medium mb-2 text-green-700 dark:text-green-300">
              Nenhum match encontrado!
            </h3>
            <p className="text-green-600 dark:text-green-400 text-sm text-center max-w-md">
              Não foi possível associar nenhum guest a um cliente real baseado no nome.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
